// Privacy Policy for Interest To Income (iTi).
// Reasonable starter — review with counsel for your jurisdiction (GDPR / CCPA / etc.).
import LegalLayout from "@/components/LegalLayout";

const LAST_UPDATED = "February 6, 2026";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated={LAST_UPDATED} testId="privacy-page">
      <p>
        This Privacy Policy explains what information Interest To Income (&ldquo;<b>iTi</b>&rdquo;)
        collects, how we use it, and the choices you have. By using the Service you agree to this
        Policy.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li>
          <b>Account info</b>: name, email, profile picture (via Google sign-in), and any business
          details you provide during onboarding.
        </li>
        <li>
          <b>Business content</b>: the prompts, edits, images, products, and pages you create.
        </li>
        <li>
          <b>Payment info</b>: handled by Stripe. We store transaction IDs and status, not full card
          numbers.
        </li>
        <li>
          <b>Usage data</b>: visits, leads, sales, and traffic sources collected via our tracking
          pixel on sites you publish. We use this to power your Analytics dashboard.
        </li>
        <li>
          <b>Device / log data</b>: IP address, user agent, and timestamps for security and
          debugging.
        </li>
        <li>
          <b>Cookies / localStorage</b>: required to keep you signed in and remember preferences
          (e.g. active business, dismissed prompts). We do not sell tracking to third parties.
        </li>
      </ul>

      <h2>2. How we use information</h2>
      <ul>
        <li>To provide and improve the Service (generate websites, plans, audience research).</li>
        <li>To process payments and manage subscriptions through Stripe.</li>
        <li>To send transactional emails (sign-up confirmation, billing receipts, team invites, order notifications) via Resend.</li>
        <li>To detect abuse, prevent fraud, and comply with the law.</li>
      </ul>

      <h2>3. Third-party processors</h2>
      <p>We share data with the minimum necessary for the Service to work:</p>
      <ul>
        <li><b>Stripe</b> &mdash; payments &amp; subscriptions (cards, billing, Connect payouts).</li>
        <li><b>Google</b> &mdash; authentication; Gemini for image generation.</li>
        <li><b>OpenAI</b> &mdash; AI text generation. Prompts may include business details you provide.</li>
        <li><b>Cloudflare</b> &mdash; domain registration and DNS for your custom domains.</li>
        <li><b>Vercel</b> &mdash; hosting and SSL for your published sites.</li>
        <li><b>Resend</b> &mdash; transactional email delivery.</li>
        <li><b>MongoDB</b> &mdash; database hosting for your account and content.</li>
      </ul>
      <p>
        Each provider operates under its own privacy policy. We do not sell your personal data and
        we do not share it with advertisers.
      </p>

      <h2>4. Your rights</h2>
      <ul>
        <li><b>Access</b> &mdash; request a copy of the personal data we hold about you.</li>
        <li><b>Correction</b> &mdash; ask us to fix inaccurate data.</li>
        <li><b>Deletion</b> &mdash; ask us to delete your account and associated data, subject to legal retention.</li>
        <li><b>Portability</b> &mdash; request your content in a portable format.</li>
        <li><b>Withdraw consent</b> &mdash; opt out of marketing emails at any time (transactional email cannot be opted out while your account is active).</li>
      </ul>
      <p>
        To exercise any right, email
        {" "}<a href="mailto:privacy@interesttoincome.com">privacy@interesttoincome.com</a>. We respond
        within 30 days.
      </p>

      <h2>5. Data retention</h2>
      <p>
        We keep account and business data as long as your account is active. After deletion, we
        purge content within 30 days, except minimal billing records we are legally required to keep
        for tax and audit purposes.
      </p>

      <h2>6. International transfers</h2>
      <p>
        We may process data in countries other than yours, including the United States. Where
        required, we rely on standard contractual clauses or equivalent safeguards.
      </p>

      <h2>7. Children</h2>
      <p>
        iTi is not directed to children under 13 (or 16 in some jurisdictions). We do not knowingly
        collect data from children. If you believe a child has provided us data, please contact us
        to have it removed.
      </p>

      <h2>8. Security</h2>
      <p>
        We use industry-standard measures (TLS in transit, encrypted databases at rest, scoped API
        keys) to protect data. No system is 100% secure; you also have a role in keeping your
        password safe.
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update this Policy. Material changes will be flagged in-app or by email. The
        &ldquo;Last updated&rdquo; date above reflects the latest revision.
      </p>

      <h2>10. Contact</h2>
      <p>
        Interest To Income<br />
        5806 Grove Ave Ste 355<br />
        Richmond, Virginia, USA<br />
        Privacy questions: <a href="mailto:privacy@interesttoincome.com">privacy@interesttoincome.com</a>.
      </p>
    </LegalLayout>
  );
}
